import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { clearCartThunk } from "../../thunks/cart";
import orderApi from "../../services/orderApi";
import config from "../../config";

function PurchaseSuccessPage() {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cart = useSelector((state: AppState) => state.cart);
  const user = useSelector((state: AppState) => state.currentUser);
  
  const [orderError, setOrderError] = useState<string | null>(null);
  const processedRef = useRef(false);
  const isProcessingRef = useRef(false);
  
  const mayaRRN = searchParams.get("id");
  const shippingParam = searchParams.get("shipping");
  const isCOD = searchParams.get("cod") === "true";
  const shippingFee = shippingParam ? parseFloat(shippingParam) : 0;
  const orderId = mayaRRN ? `${config.branding.orderIdPrefix}-${mayaRRN.substring(0, 8).toUpperCase()}` : null;

  const processOrder = useCallback(async () => {
    if (isProcessingRef.current || processedRef.current) return;
    if (!mayaRRN) {
      if (cart.items.length === 0) {
        navigate("/", { replace: true });
      }
      return;
    }

    isProcessingRef.current = true;
    processedRef.current = true;

    if (isCOD) {
      dispatch(clearCartThunk());
      navigate(`/order?id=${orderId}&status=success`, { replace: true });
      return;
    }

    let orderCreated = false;
    try {
      const currentCart = cart;
      const currentUser = user;
      
      const orderData = {
        customerName: `${currentUser.firstName || 'Customer'} ${currentUser.lastName || ''}`.trim(),
        email: currentUser.email || '',
        phone: currentUser.phone || '',
        address: currentUser.address || '',
        city: currentUser.city || '',
        province: currentUser.province || '',
        items: currentCart.items.map((item: any) => ({
          name: item.product.name,
          quantity: item.quantity,
          unitPrice: item.product.unitPrice || item.product.price || 0,
          totalPrice: item.totalPrice,
          imageUrl: item.product.imageUrl || item.product.image || ''
        })),
        totalAmount: currentCart.totalAmount + shippingFee,
        shippingFee: shippingFee,
        paymentStatus: 'paid',
        orderStatus: 'processing',
        mayaRRN: mayaRRN,
        hostUrl: config.host_url
      };
      
      const orderResponse = await orderApi.createOrder(orderData);
      
      if (!orderResponse.success) {
        throw new Error(orderResponse.error || "Failed to create order");
      }
      orderCreated = true;
      
      for (const item of currentCart.items) {
        if (item.product.id && mayaRRN) {
          let retries = 3;
          while (retries > 0) {
            try {
              const result = await orderApi.updateStock(item.product.id, item.quantity, mayaRRN);
              if (result.success) break;
              retries--;
              if (retries > 0) await new Promise(r => setTimeout(r, 500));
            } catch (error) {
              console.error(`Error updating stock for product ${item.product.id}:`, error);
              retries--;
              if (retries > 0) await new Promise(r => setTimeout(r, 500));
            }
          }
        }
      }
      
      dispatch(clearCartThunk());
    } catch (error: any) {
      console.error("Error creating order:", error);
      setOrderError(error.message || "Failed to save order. Please contact support with your order reference: " + orderId);
      isProcessingRef.current = false;
      processedRef.current = false;
      return;
    }
    
    if (orderCreated && orderId) {
      navigate(`/order?id=${orderId}&status=success`, { replace: true });
    } else {
      navigate("/", { replace: true });
    }
  }, [mayaRRN, isCOD, cart, user, shippingFee, orderId, dispatch, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      processOrder();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [processOrder]);

  if (orderError) {
    return (
      <div style={{ background: "#f9fafb", minHeight: "100vh", padding: "40px 20px", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <div style={{ background: "white", padding: "40px", borderRadius: "16px", textAlign: "center", maxWidth: "500px" }}>
          <div style={{ width: "60px", height: "60px", borderRadius: "50%", background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <i className="fas fa-exclamation-triangle" style={{ fontSize: "24px", color: "#dc2626" }}></i>
          </div>
          <h2 style={{ color: "#1a1a1a", marginBottom: "8px" }}>Order Error</h2>
          <p style={{ color: "#374151", marginBottom: "16px" }}>{orderError}</p>
          <p style={{ color: "#6b7280", fontSize: "0.9rem", marginBottom: "24px" }}>
            Your payment was received. Please save this reference number and contact support: <strong>{mayaRRN}</strong>
          </p>
          <button 
            onClick={() => navigate("/", { replace: true })}
            style={{ background: "#6366f1", color: "white", border: "none", padding: "12px 24px", borderRadius: "8px", cursor: "pointer", fontWeight: "600" }}
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#f9fafb", minHeight: "100vh", padding: "40px 20px", display: "flex", justifyContent: "center", alignItems: "center" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: "40px", height: "40px", border: "3px solid #e5e7eb", borderTop: "3px solid #6366f1", borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 16px" }}></div>
        <p style={{ color: "#374151" }}>Processing your order...</p>
      </div>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default PurchaseSuccessPage;
